// ============================================================
// systems/bombs.js — Bomb hazard system (500+ clicks)
// ============================================================
import { state } from '../state.js';
import { dom } from '../dom.js';
import { startFireEffect, stopFireEffect } from './fire.js';
import { applyHazardDamage } from './competitive.js';
import { startDarkMusic } from '../audio/musicDark.js';
import { startMusic } from '../audio/musicChill.js';
import { startOgMusic } from '../audio/musicOg.js';

// ---- Helpers ----

function startCurrentMusic() {
  if (state.musicSource === 'mp3') { startMusic(); }
  else { startOgMusic(); }
}

// ---- Public API ----

export function activateBombMode() {
  if (state.bombMode) return;
  state.bombMode = true;

  // Burning plushie
  dom.smiley.classList.add('burning');
  startFireEffect();

  // Dark background
  document.body.classList.add('danger-mode');

  // Warning banner
  dom.warningEl.classList.add('show');
  setTimeout(() => dom.warningEl.classList.remove('show'), 3000);

  // Switch to dark music if music was playing
  if (state.musicPlaying) {
    startDarkMusic();
  }

  // Play alarm sound
  if (state.audioCtx) {
    const now = state.audioCtx.currentTime;
    for (let i = 0; i < 3; i++) {
      const osc = state.audioCtx.createOscillator();
      osc.type = 'square';
      osc.frequency.value = 600;
      const env = state.audioCtx.createGain();
      env.gain.setValueAtTime(0.08, now + i * 0.3);
      env.gain.exponentialRampToValueAtTime(0.001, now + i * 0.3 + 0.2);
      osc.connect(env).connect(state.audioCtx.destination);
      osc.start(now + i * 0.3);
      osc.stop(now + i * 0.3 + 0.25);
    }
  }

  // Start spawning bombs
  spawnBomb();
  state.bombInterval = setInterval(spawnBomb, 2000 + Math.random() * 1500);
  state.bombAnimFrame = requestAnimationFrame(updateBombs);
}

export function spawnBomb() {
  if (!state.bombMode || state.paused) return;
  const bomb = document.createElement('div');
  bomb.className = 'bomb';
  bomb.textContent = '\uD83D\uDCA3';

  // Spawn from random edge
  const edge = Math.floor(Math.random() * 4);
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let x, y, vx, vy;
  const speed = 1.2 + Math.random() * 1.5;

  if (edge === 0) { x = Math.random() * vw; y = -50; }       // top
  else if (edge === 1) { x = Math.random() * vw; y = vh + 50; } // bottom
  else if (edge === 2) { x = -50; y = Math.random() * vh; }    // left
  else { x = vw + 50; y = Math.random() * vh; }                // right

  // Aim toward center area with some randomness
  const targetX = vw / 2 + (Math.random() - 0.5) * vw * 0.6;
  const targetY = vh / 2 + (Math.random() - 0.5) * vh * 0.6;
  const angle = Math.atan2(targetY - y, targetX - x);
  vx = Math.cos(angle) * speed;
  vy = Math.sin(angle) * speed;

  bomb.style.transform = `translate(${x}px, ${y}px)`;
  document.body.appendChild(bomb);

  state.bombs.push({ el: bomb, x, y, vx, vy });
}

export function updateBombs() {
  if (!state.bombMode) return;
  if (state.paused) { state.bombAnimFrame = requestAnimationFrame(updateBombs); return; }

  for (let i = state.bombs.length - 1; i >= 0; i--) {
    const b = state.bombs[i];
    b.x += b.vx;
    b.y += b.vy;
    b.el.style.transform = `translate(${b.x}px, ${b.y}px)`;

    // Check if cursor touches this bomb — subtract 35 clicks
    const bCenterX = b.x + 25;
    const bCenterY = b.y + 25;
    const dist = Math.hypot(state.mouseX - bCenterX, state.mouseY - bCenterY);
    if (dist < 35) {
      // Subtract clicks
      applyHazardDamage(35);

      // Explosion effect at bomb location
      const exp = document.createElement('div');
      exp.className = 'explosion';
      exp.textContent = '\uD83D\uDCA5';
      exp.style.left = (b.x - 10) + 'px';
      exp.style.top = (b.y - 10) + 'px';
      document.body.appendChild(exp);
      setTimeout(() => exp.remove(), 600);

      // Show -35 floating text
      const dmg = document.createElement('div');
      dmg.className = 'particle';
      dmg.textContent = '-35';
      dmg.style.left = b.x + 'px';
      dmg.style.top = b.y + 'px';
      dmg.style.color = '#ff4444';
      dmg.style.fontSize = '32px';
      document.body.appendChild(dmg);
      setTimeout(() => dmg.remove(), 800);

      // Hit sound
      if (state.audioCtx) {
        const now = state.audioCtx.currentTime;
        const noise = state.audioCtx.createBufferSource();
        const buf = state.audioCtx.createBuffer(1, state.audioCtx.sampleRate * 0.2, state.audioCtx.sampleRate);
        const nd = buf.getChannelData(0);
        for (let j = 0; j < nd.length; j++) {
          nd[j] = (Math.random() * 2 - 1) * Math.pow(1 - j / nd.length, 2);
        }
        noise.buffer = buf;
        const env = state.audioCtx.createGain();
        env.gain.setValueAtTime(0.2, now);
        env.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
        noise.connect(env).connect(state.audioCtx.destination);
        noise.start(now);
        noise.stop(now + 0.25);
      }

      // Flash screen red briefly
      document.body.style.boxShadow = 'inset 0 0 80px rgba(255,0,0,0.4)';
      setTimeout(() => document.body.style.boxShadow = '', 200);

      // Remove this bomb
      b.el.remove();
      state.bombs.splice(i, 1);

      // If score drops below thresholds — use lazy imports to avoid circular deps
      checkPhaseDowngrades();
      continue;
    }

    // Remove if far off screen
    if (b.x < -150 || b.x > window.innerWidth + 150 ||
        b.y < -150 || b.y > window.innerHeight + 150) {
      b.el.remove();
      state.bombs.splice(i, 1);
    }
  }

  state.bombAnimFrame = requestAnimationFrame(updateBombs);
}

/**
 * Check if score dropped below phase thresholds and deactivate accordingly.
 * Uses dynamic import-like approach via state flags — the actual deactivation
 * functions are registered by each phase module at init time.
 *
 * To avoid circular dependencies, we check state flags and call deactivation
 * through the registry pattern. Each phase module registers its deactivation
 * callback on the shared phaseDeactivators map.
 */
function checkPhaseDowngrades() {
  if (state.score < 7500 && state.phase5Active && _deactivators.phase5) { _deactivators.phase5(); }
  if (state.score < 6000 && state.phase4Active && _deactivators.phase4) { _deactivators.phase4(); }
  if (state.score < 3500 && state.phase3Active && _deactivators.phase3) { _deactivators.phase3(); }
  if (state.score < 1500 && state.frozenMode && _deactivators.frozen) { _deactivators.frozen(); }
  if (state.score < 500) {
    dom.smiley.classList.remove('burning');
    stopFireEffect();
    document.body.classList.remove('danger-mode');
    state.bombMode = false;
    state.darkMusicPlaying = false;
    clearInterval(state.bombInterval);
    state.musicNodes.forEach(n => { try { n.disconnect(); } catch(e){} });
    state.musicNodes = [];
    state.musicPlaying = false;
    startCurrentMusic();
    state.bombs.forEach(bb => bb.el.remove());
    state.bombs = [];
  }
}

// ---- Deactivator registry (avoids circular imports) ----
const _deactivators = {};

/**
 * Register a phase deactivation callback.
 * Called by each phase module during initialisation.
 * @param {'frozen'|'phase3'|'phase4'|'phase5'} key
 * @param {Function} fn
 */
export function registerDeactivator(key, fn) {
  _deactivators[key] = fn;
}

/**
 * Shared helper: check phase downgrades from any hazard collision.
 * Exported so frozen.js, phase3Attacks.js, etc. can call it after damage.
 */
export { checkPhaseDowngrades };
